import type {
    BullionBonusPick,
    BullionBonusPicks,
    BullionMarket,
    BullionRoom,
    BullionSelection,
    BullionSelectionMap,
} from "./types";
import { BULLION_BONUS_POINTS_BY_TIER } from "./constants";

export function bullionSelectionRegularChips(s: BullionSelection): number {
    return s.chips;
}

export function totalRegularChips(selections: BullionSelectionMap): number {
    return Object.values(selections).reduce((sum, s) => sum + bullionSelectionRegularChips(s), 0);
}

export function bullionSelectionStorageKey(market: Pick<BullionMarket, "id" | "decimalMarketId">): string {
    const raw = market.decimalMarketId;
    const mid = raw != null && String(raw).trim() !== "" ? String(raw).trim() : "";
    return mid || market.id;
}

export function normalizeBullionSelectionMapKeys(room: BullionRoom, picks: BullionSelectionMap): BullionSelectionMap {
    const flat = room.groups.flatMap((g) => g);
    const out: BullionSelectionMap = {};
    for (const [key, pick] of Object.entries(picks)) {
        const market =
            flat.find((m) => bullionSelectionStorageKey(m) === key || m.id === key) ||
            flat.find((m) => String(m.decimalMarketId ?? "").trim() === String(pick.decimalMarketId ?? "").trim()) ||
            flat.find((m) => String(m.decimalMarketId ?? "").trim() === String(pick.marketId ?? "").trim()) ||
            undefined;
        const sk = market
            ? bullionSelectionStorageKey(market)
            : String(pick.decimalMarketId ?? "").trim() || String(pick.marketId ?? "").trim() || key;
        out[sk] = { ...pick, marketId: sk };
    }
    return out;
}

function normalizeBullionBonusPick(room: BullionRoom, bonus: BullionBonusPick): BullionBonusPick {
    const flat = room.groups.flatMap((g) => g);
    const market =
        flat.find((m) => bullionSelectionStorageKey(m) === bonus.marketId || m.id === bonus.marketId) ||
        flat.find((m) => String(m.decimalMarketId ?? "").trim() === String(bonus.decimalMarketId ?? "").trim()) ||
        undefined;
    if (!market) return bonus;
    return { ...bonus, marketId: bullionSelectionStorageKey(market) };
}

export function normalizeBullionBonusPicks(room: BullionRoom, bonuses: BullionBonusPicks | null | undefined): BullionBonusPicks {
    if (!Array.isArray(bonuses) || bonuses.length === 0) return [];
    return bonuses.filter(Boolean).map((b) => normalizeBullionBonusPick(room, b));
}

/** Normalize an unknown bonus payload (array, or legacy single `goldPick`) into a `BullionBonusPicks` array. */
export function coerceBullionBonusPicks(raw: unknown): BullionBonusPicks {
    if (Array.isArray(raw)) {
        return raw
            .filter((b): b is BullionBonusPick => Boolean(b) && typeof b === "object")
            .map((b) => {
                const tier = b.tier === "small" || b.tier === "large" ? b.tier : "large";
                // Always resolve stake from the live tier table so legacy slips stored as
                // 15/45 (pre-scale) score as 150/450 after the pts/chip raise.
                return { ...b, tier, points: BULLION_BONUS_POINTS_BY_TIER[tier] } as BullionBonusPick;
            });
    }
    // Legacy single gold chip — represent as one bonus so old slips still render a bonus marker.
    if (raw && typeof raw === "object") {
        const g = raw as Record<string, unknown>;
        if (g.runnerId != null && g.marketId != null) {
            return [
                {
                    tier: "large",
                    points: BULLION_BONUS_POINTS_BY_TIER.large,
                    marketId: String(g.marketId),
                    runnerId: String(g.runnerId),
                    groupIndex: Number(g.groupIndex ?? 0),
                    decimalMarketId: (g.decimalMarketId as string | null) ?? null,
                    decimalMarketLineId: (g.decimalMarketLineId as string | null) ?? null,
                    marketName: (g.marketName as string | null) ?? null,
                    selectionName: (g.selectionName as string | null) ?? null,
                },
            ];
        }
    }
    return [];
}

export function regularChipsInGroup(
    selections: BullionSelectionMap,
    groupIndex: number,
    excludeMarketId?: string
): number {
    return Object.entries(selections)
        .filter(([id, s]) => s.groupIndex === groupIndex && id !== excludeMarketId)
        .reduce((sum, [, s]) => sum + bullionSelectionRegularChips(s), 0);
}

export function parseBullionPlaySelectionsFromApi(
    raw: unknown
): { picks: BullionSelectionMap; bonusPicks: BullionBonusPicks } | null {
    if (raw == null) return null;
    let s: unknown = raw;
    if (typeof s === "string") {
        try {
            s = JSON.parse(s);
        } catch {
            return null;
        }
    }
    if (!s || typeof s !== "object" || Array.isArray(s)) return null;
    const obj = s as Record<string, unknown>;
    if (obj.picks && typeof obj.picks === "object" && !Array.isArray(obj.picks)) {
        return {
            picks: obj.picks as BullionSelectionMap,
            bonusPicks: coerceBullionBonusPicks(obj.bonusPicks ?? obj.goldPick),
        };
    }
    const bonusPicks = coerceBullionBonusPicks(obj.bonusPicks ?? obj.goldPick);
    const flat: BullionSelectionMap = {};
    for (const [k, v] of Object.entries(obj)) {
        if (k === "goldPick" || k === "bonusPicks") continue;
        if (!v || typeof v !== "object" || Array.isArray(v)) continue;
        const p = v as Record<string, unknown>;
        if (p.runnerId != null && p.marketId != null) flat[k] = v as BullionSelection;
    }
    if (Object.keys(flat).length) return { picks: flat, bonusPicks };
    return null;
}
